// Fuente única de verdad para los colores del tema claro/oscuro del
// rediseño (piloto: módulo Eventos + Sidebar). La requiere directamente
// `tailwind.config.js` (Node/CommonJS) para generar las clases de Tailwind,
// y la importan los componentes que necesitan el valor literal en JS (MUI
// `createTheme`/`makeStyles`, `PaperProps` de `Dialog`/`Drawer`, animaciones
// de `framer-motion`) — así ningún color del tema queda hardcodeado "in
// place" en más de un lugar. Ver `docs/design-system-proposal.md` sección 2.
//
// Paleta del cliente (Pantone 301 / `docs/colores-cliente..png`): los
// RELLENOS, FONDOS, BORDES, CHIPS y GRADIENTES usan las dos familias que
// pasó el cliente — el anillo derecho (celestes) para el TEMA CLARO y el
// anillo izquierdo (navy/periwinkle) para el TEMA OSCURO. Los COLORES DE
// ACCIÓN (botones rellenos, toggles ON, links, foco de inputs) se mantienen
// en el cyan original a pedido: `accent`/`accentHover`/`accentSoft*`/`glow`
// y los shades `secondary` 500→900. Solo se relightearon los shades
// `secondary` 50→400 (fills/bordes/chips neutros) con la familia celeste.

// Escala `secondary`:
//   50→400 = familia celeste del cliente (anillo derecho) — rellenos,
//            bordes, chips, fondos suaves (tema claro; y como acentos
//            claros sobre navy en tema oscuro).
//   500→900 = CYAN de acción original, sin cambios (botones/toggles/links).
const secondary = {
  50: '#e4eff8', // cliente
  100: '#d0eaf3', // cliente
  200: '#b9ddef', // cliente
  300: '#aed7ef', // cliente
  400: '#97d3ec', // cliente
  500: '#0ea5e9', // acción — sin cambios
  600: '#0284c7', // acción — sin cambios
  700: '#0369a1', // sin cambios
  800: '#075985', // sin cambios
  900: '#0c4a6e' // sin cambios
};

const darkTokens = {
  50: '#B4B5CA',
  100: '#A1A1C1',
  200: '#8D8DB2',
  300: '#777EA3',
  400: '#5F6R99',
  500: '#47608A',
  600: '#004A7F'
}

const lightTokens = {
  50: '#E4EFF8',
  100: '#D0EAF3',
  200: '#C4E4ED',
  300: '#B9DDEF',
  400: '#AED7EF',
  500: '#97D3EC',
  600: '#6CC3E8'
}

const others = {
  whiteLight: '#F2F9FF',
  darkLight: '#00406E',
  darkLightHover: '#00233D',
}



// TEMA CLARO — familia celeste del cliente (anillo derecho):
// #6CC3E8 #97D3EC #AED7EF #B9DDEF #C4E4ED #D0EAF3 #E4EFF8
const light = {
  textPrimary: darkTokens[600], // texto — sin cambios (legibilidad)
  textSecondary: '#516073', // texto — sin cambios
  textDisabled: 'rgba(15, 23, 42, 0.3)',
  border: '#C4E4ED', // cliente (antes #E2E8F0)
  borderHover: secondary[300], // cliente #AED7EF
  surface: others.whiteLight, // card blanca sólida — sin cambios
  surfaceMuted: 'rgba(255, 255, 255, 0.05)',
  accent: secondary[500], // acción — sin cambios (#0EA5E9)
  accentHover: secondary[600], // acción — sin cambios (#0284C7)
  accentSoft: '#F0F9FF', // soft de acción — sin cambios
  accentSoftStrong: '#E0F2FE', // soft de acción — sin cambios
  glow: '0 0 16px rgba(255, 255, 255, 255)',
  pageGradient: `linear-gradient(to right, ${lightTokens[50]} 0%, ${lightTokens[50]} 100%)`,
  headerPageGradient:
    `linear-gradient(to right, ${lightTokens[50]} 0%, ${lightTokens[50]} 100%)`,
  sidebarMidGradient: `linear-gradient(to right, ${lightTokens[50]} 50%, ${lightTokens[50]} 100%)`,
  sidebarEdgeGradient: `linear-gradient(to right, ${lightTokens[50]} 50%, ${lightTokens[50]} 100%)`,
  generalCardGradient:
    `linear-gradient(to right, #ffffff 0%, ${others.whiteLight} 100%)`,
  deleteGradient:
    `linear-gradient(to right, ${others.whiteLight} 75%, #FF0000 100%)`,
  themeToggleSky: `linear-gradient(135deg, ${lightTokens[500]} 0%, #E4EFF8 100%)`,
  overlay: 'rgba(15, 23, 42, 0.4)',
  modalBackground: lightTokens[50]
};

// TEMA OSCURO — familia navy/periwinkle del cliente (anillo izquierdo):
// #004A7F #47608A #5F6E99 #777EA3 #8D8DB2 #A1A1C1 #B4B5CA
const dark = {
  textPrimary: '#F7FAFF', // texto — sin cambios (legibilidad)
  textSecondary: darkTokens[50], // cliente periwinkle-gris (antes #94A3B8)
  textDisabled: 'rgba(248, 250, 252, 0.3)',
  border: 'rgba(255, 255, 255, 0.1)', // borde glass — sin cambios (= border-white/10)
  borderHover: secondary[400], // cliente #97D3EC
  surface: `${others.darkLight}` , // cliente navy base (antes #00163B)
  surfacePaper: darkTokens[500], // cliente navy panel (antes #012A4A)
  surfaceMuted: 'rgba(255, 255, 255, 0.05)', // glass — sin cambios
  accent: '#22D3EE', // acción — sin cambios
  accentHover: '#38BDF8', // acción — sin cambios (antes secondary[400])
  accentSoft: 'rgba(14, 165, 233, 0.15)', // soft de acción — sin cambios
  accentSoftStrong: 'rgba(14, 165, 233, 0.25)', // soft de acción — sin cambios
  glow: '0 0 16px rgba(34, 211, 238, 0.08)', // glow de acción — sin cambios
  insetGlow: '0 0 14px rgba(180, 181, 202)',
  pageGradient:
    `linear-gradient(to bottom right, ${darkTokens[600]} 0%, ${darkTokens[600]} 100%)`,
  headerPageGradient:
    `linear-gradient(to right, ${darkTokens[600]} 80%, ${darkTokens[600]} 90%)`,
  sidebarMidGradient: `linear-gradient(to right, ${darkTokens[600]} 85%, ${darkTokens[600]} 100%)`,
  sidebarEdgeGradient: `linear-gradient(to right, ${darkTokens[600]} 85%, ${darkTokens[600]} 100%)`,
  sidebarFooterGradient: `linear-gradient(to right, ${darkTokens[600]} 85%, ${darkTokens[600]} 100%)`,
  generalCardGradient:
    `linear-gradient(to right, ${others.darkLightHover} 0%, ${others.darkLight} 100%)`,
  deleteGradient:
    `linear-gradient(to right, ${others.darkLight} 0%, ${others.darkLight} 75%, #FF0000 100%)`,
  themeToggleSky: `linear-gradient(135deg, ${darkTokens[600]} 0%, ${darkTokens[500]} 100%)`,
  overlay: 'rgba(0, 74, 127, 0.6)',
  modalBackground: darkTokens[600]
};

// Colores de estado semánticos (semáforos, estados de chofer, feedback).
// Cada rol tiene la variante para TEMA CLARO y una variante "flúor" (más
// brillante/saturada) para conservar contraste sobre el fondo navy del
// TEMA OSCURO — el mismo color, más neón. Alineado con la semántica de las
// tablas de la sección 2 del design-system-proposal.
// Uso en Tailwind: `text-success dark:text-success-dark`, etc.
const status = {
  success: { light: '#10B981', dark: '#34D399' },
  warning: { light: '#F59E0B', dark: '#FBBF24' },
  danger: { light: '#EF4444', dark: '#F87171' },
  info: { light: '#0EA5E9', dark: '#38BDF8' }
};

// Estilo único de los campos de formulario (input, textarea, label, error).
// No redeclararlos en cada componente: usar `FormField`/`FormikInput`
// (`shared/components/form`) o importar estas clases.
const inputClass =
  'w-full rounded-lg border border-line bg-whiteLight px-3 py-2 text-sm text-ink outline-none transition focus:border-secondary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-darkLight dark:text-ink-dark';
const labelClass =
  'mb-1 block text-xs font-medium text-inkMuted dark:text-inkMuted-dark';
const errorClass = 'mt-1 text-xs text-red-500';

module.exports = { secondary, light, dark, darkTokens, lightTokens, status, others, inputClass, labelClass, errorClass };
